import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { a200Estimator } from '../../content/shopContent';
import { toCents, fmt, incFromEx } from '../../lib/shopPricing';
import TradeQuoteForm from './TradeQuoteForm';

// AHS A200 packages: tick options → estimate, then book the site visit that turns
// it into a fixed price and a signed contract. Nothing here is charged online.
export default function A200Estimator({ product }) {
  const [opts, setOpts] = useState({});
  const [variantIdx, setVariantIdx] = useState(0);
  // Wireless activation points: the package fixes how many, the customer picks which (mix and match).
  const slots = product.activationSlots || 0;
  const [activation, setActivation] = useState(() => (product.activationDefault || []).slice(0, slots));
  const setSlot = (i, v) => setActivation((a) => { const next = a.slice(); next[i] = v; return next; });
  const toggle = (k) => setOpts((o) => ({ ...o, [k]: !o[k] }));
  const base = toCents(product.priceIncGst);
  // The supply and install lines behind the package price, the way we quote them.
  const lines = product.packageLines || [];
  const linesExCents = lines.reduce((sum, l) => sum + toCents(l[1]), 0);
  const linesAddUp = lines.length > 0 && Math.abs(incFromEx(linesExCents) - base) <= lines.length * 50;
  const extras = a200Estimator.options.filter((o) => opts[o.key]).reduce((sum, o) => sum + toCents(o.addIncGst), 0);
  const estimate = base + extras;
  const variant = product.variants[variantIdx] || product.variants[0];
  const chosen = a200Estimator.options.filter((o) => opts[o.key]).map((o) => o.label);
  const message = [
    `Site visit request: ${product.name}`,
    `Option: ${variant.label}`,
    ...(slots ? [`Activation points: ${activation.join(', ')}`] : []),
    chosen.length ? `Ticked: ${chosen.join('; ')}` : 'Ticked: none',
    `Online estimate: ${fmt(estimate)} incl. GST`,
    '',
    'Address / door / best time for the visit: ',
  ].join('\n');

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 mb-6" data-testid="a200-estimator">
      <p className="font-bold text-gray-900">Package estimate: {fmt(estimate)} incl. GST</p>
      <p className="text-sm text-gray-700 mt-1">{a200Estimator.intro}</p>
      {linesAddUp && (
        <details className="mt-3 rounded-lg bg-white border border-gray-200" data-testid="a200-lines">
          <summary className="px-3 py-2 text-sm font-semibold text-gray-900 cursor-pointer select-none">What is in the {fmt(base)}</summary>
          <table className="w-full text-sm">
            <tbody>
              {lines.map((l) => (
                <tr key={l[0]} className="border-t border-gray-100">
                  <td className="px-3 py-1.5 text-gray-700">{l[0]}</td>
                  <td className="px-3 py-1.5 text-right text-gray-900 whitespace-nowrap">{fmt(toCents(l[1]))}</td>
                </tr>
              ))}
              <tr className="border-t border-gray-200 font-semibold">
                <td className="px-3 py-1.5 text-gray-900">Package, ex GST</td>
                <td className="px-3 py-1.5 text-right text-gray-900 whitespace-nowrap">{fmt(linesExCents)}</td>
              </tr>
              <tr>
                <td className="px-3 py-1.5 text-gray-700">GST 15%</td>
                <td className="px-3 py-1.5 text-right text-gray-900 whitespace-nowrap">{fmt(base - linesExCents)}</td>
              </tr>
              <tr className="border-t border-gray-200 font-bold">
                <td className="px-3 py-1.5 text-gray-900">Package, incl. GST</td>
                <td className="px-3 py-1.5 text-right text-gray-900 whitespace-nowrap">{fmt(base)}</td>
              </tr>
            </tbody>
          </table>
          <p className="px-3 pb-2 text-xs text-gray-600">Supply-and-install only: we do not sell the operator on its own.</p>
        </details>
      )}
      {product.variants.length > 1 && (
        <div className="mt-4">
          <p className="font-semibold text-gray-900 mb-2">Arm / activation</p>
          <div className="flex flex-wrap gap-2">
            {product.variants.map((v, i) => (
              <button key={v.id} type="button" onClick={() => setVariantIdx(i)} className={`px-3 py-2 rounded-full text-sm border ${i === variantIdx ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-300 text-gray-800 hover:border-gray-500'}`}>
                {v.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {slots > 0 && (
        <div className="mt-4" data-testid="a200-activation">
          <p className="font-semibold text-gray-900 mb-1">Your {slots} activation points</p>
          <p className="text-sm text-gray-700 mb-2">Any mix — two remotes, two hand sensors, one of each. Change any of them.</p>
          <div className="grid sm:grid-cols-3 gap-2">
            {Array.from({ length: slots }).map((_, i) => (
              <label key={i} className="block text-sm">
                <span className="block text-xs font-medium uppercase tracking-wide text-gray-500 mb-1">Point {i + 1}</span>
                <select value={activation[i] || ''} onChange={(e) => setSlot(i, e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2" data-testid={`a200-activation-${i}`}>
                  {(product.activationChoices || []).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="mt-4 space-y-2">
        {a200Estimator.options.map((o) => (
          <label key={o.key} className="flex items-start gap-3 rounded-lg bg-white border border-gray-200 p-3 cursor-pointer">
            <input type="checkbox" checked={!!opts[o.key]} onChange={() => toggle(o.key)} className="mt-1 h-4 w-4" data-testid={`a200-opt-${o.key}`} />
            <span>
              <span className="font-semibold text-gray-900">
                {o.label} {o.addIncGst > 0 ? <span className="text-gray-600 font-normal">+{fmt(toCents(o.addIncGst))}</span> : <span className="text-gray-600 font-normal">quoted at the visit</span>}
              </span>
              <span className="block text-sm text-gray-700">{o.note}</span>
              {o.video && (
                <details className="mt-2" onClick={(e) => e.stopPropagation()} data-testid={`a200-opt-${o.key}-video`}>
                  <summary className="text-sm text-red-600 font-semibold cursor-pointer select-none">See the included capping on a real install</summary>
                  <video controls playsInline preload="none" className="block max-w-full max-h-[60vh] rounded-xl border border-gray-200 mt-2 bg-black">
                    <source src={o.video.src} type="video/mp4" />
                  </video>
                  <span className="block text-xs text-gray-600 mt-1">{o.video.caption}</span>
                </details>
              )}
            </span>
          </label>
        ))}
      </div>
      <p className="text-xs text-gray-600 mt-3">{a200Estimator.contract}</p>
      <div className="mt-5 rounded-2xl border border-gray-200 bg-white p-5">
        <h2 className="text-lg font-bold text-gray-900 mb-1">{a200Estimator.visit}</h2>
        <p className="text-sm text-gray-700 mb-3">
          We come out, measure the door and frame, check the power and the cabling route, and leave you a fixed price. Servicing can be added at the same time.{' '}
          <Link to="/shop/door-automation" className="text-red-600 font-semibold">All packages</Link>
        </p>
        <TradeQuoteForm pageSource="shop-a200-visit" projectType="A200 site visit" initialMessage={message} submitLabel="Book a site visit" thanks="Thanks — we will call to set a time for the visit." />
      </div>
    </div>
  );
}
